import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';

// Configure axios with basic auth
const api = axios.create({
  baseURL: 'https://api.gilliannewton.com/api/v1',
  auth: {
    username: 'admin',
    password: 'admin'
  },
  headers: {
    'Content-Type': 'application/json'
  }
});

function App() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('accounts');

  // Form states
  const [newAccount, setNewAccount] = useState({
    accountHolderName: '',
    initialBalance: '',
    currency: 'USD'
  });

  const [transfer, setTransfer] = useState({
    fromAccountNumber: '',
    toAccountNumber: '',
    amount: '',
    description: ''
  });

  // Fetch accounts
  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/accounts');
      setAccounts(response.data);
      setError('');
    } catch (err) {
      setError('Failed to fetch accounts. Please check your connection.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  // Create account
  const handleCreateAccount = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      await api.post('/accounts', {
        accountHolderName: newAccount.accountHolderName,
        initialBalance: parseFloat(newAccount.initialBalance),
        currency: newAccount.currency
      });
      setSuccess('Account created successfully!');
      setNewAccount({ accountHolderName: '', initialBalance: '', currency: 'USD' });
      fetchAccounts();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  // Execute transfer
  const handleTransfer = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      const response = await api.post('/transfers', {
        fromAccountNumber: transfer.fromAccountNumber,
        toAccountNumber: transfer.toAccountNumber,
        amount: parseFloat(transfer.amount),
        description: transfer.description
      });
      setSuccess(`Transfer completed! Transaction ID: ${response.data.transactionId}`);
      setTransfer({ fromAccountNumber: '', toAccountNumber: '', amount: '', description: '' });
      fetchAccounts();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.message || 'Transfer failed');
    } finally {
      setLoading(false);
    }
  };

  // Get account balance
  const getAccountBalance = (accountNumber) => {
    const account = accounts.find(acc => acc.accountNumber === accountNumber);
    return account ? account.balance : 0;
  };

  return (
    <div className="app">
      <header className="header">
        <div className="container">
          <h1>🏦 Banking Fund Transfer</h1>
          <p>Secure & Fast Money Transfers</p>
        </div>
      </header>

      <main className="container">
        {/* Alerts */}
        {error && (
          <div className="alert alert-error">
            <span>❌</span> {error}
          </div>
        )}
        {success && (
          <div className="alert alert-success">
            <span>✅</span> {success}
          </div>
        )}

        {/* Tabs */}
        <div className="tabs">
          <button 
            className={activeTab === 'accounts' ? 'active' : ''} 
            onClick={() => setActiveTab('accounts')}
          >
            📊 Accounts
          </button>
          <button 
            className={activeTab === 'create' ? 'active' : ''} 
            onClick={() => setActiveTab('create')}
          >
            ➕ Create Account
          </button>
          <button 
            className={activeTab === 'transfer' ? 'active' : ''} 
            onClick={() => setActiveTab('transfer')}
          >
            💸 Transfer Funds
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {/* Accounts List */}
          {activeTab === 'accounts' && (
            <div className="accounts-section">
              <div className="section-header">
                <h2>All Accounts</h2>
                <button onClick={fetchAccounts} className="btn btn-secondary" disabled={loading}>
                  🔄 Refresh
                </button>
              </div>

              {loading ? (
                <div className="loading">Loading accounts...</div>
              ) : accounts.length === 0 ? (
                <div className="empty-state">
                  <p>No accounts yet. Create your first account to get started!</p>
                </div>
              ) : (
                <div className="accounts-grid">
                  {accounts.map((account) => (
                    <div key={account.id} className="account-card">
                      <div className="account-header">
                        <h3>{account.accountHolderName}</h3>
                        <span className={`status ${account.status.toLowerCase()}`}>
                          {account.status}
                        </span>
                      </div>
                      <div className="account-number">
                        Account: <code>{account.accountNumber}</code>
                      </div>
                      <div className="account-balance">
                        <span className="label">Balance</span>
                        <span className="amount">
                          ${account.balance.toFixed(2)} {account.currency}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Create Account Form */}
          {activeTab === 'create' && (
            <div className="form-section">
              <h2>Create New Account</h2>
              <form onSubmit={handleCreateAccount} className="form">
                <div className="form-group">
                  <label htmlFor="accountHolderName">Account Holder Name *</label>
                  <input
                    type="text"
                    id="accountHolderName"
                    value={newAccount.accountHolderName}
                    onChange={(e) => setNewAccount({ ...newAccount, accountHolderName: e.target.value })}
                    placeholder="Enter full name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="initialBalance">Initial Balance *</label>
                  <input
                    type="number"
                    id="initialBalance"
                    value={newAccount.initialBalance}
                    onChange={(e) => setNewAccount({ ...newAccount, initialBalance: e.target.value })}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="currency">Currency</label>
                  <select
                    id="currency"
                    value={newAccount.currency}
                    onChange={(e) => setNewAccount({ ...newAccount, currency: e.target.value })}
                  >
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="GBP">GBP - British Pound</option>
                  </select>
                </div>

                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Creating...' : '➕ Create Account'}
                </button>
              </form>
            </div>
          )}

          {/* Transfer Form */}
          {activeTab === 'transfer' && (
            <div className="form-section">
              <h2>Transfer Funds</h2>
              
              {accounts.length < 2 ? (
                <div className="empty-state">
                  <p>You need at least 2 accounts to make a transfer.</p>
                  <button 
                    className="btn btn-primary" 
                    onClick={() => setActiveTab('create')}
                  >
                    Create Account
                  </button>
                </div>
              ) : (
                <form onSubmit={handleTransfer} className="form">
                  <div className="form-group">
                    <label htmlFor="fromAccount">From Account *</label>
                    <select
                      id="fromAccount"
                      value={transfer.fromAccountNumber}
                      onChange={(e) => setTransfer({ ...transfer, fromAccountNumber: e.target.value })}
                      required
                    >
                      <option value="">Select account</option>
                      {accounts.map((account) => (
                        <option key={account.id} value={account.accountNumber}>
                          {account.accountHolderName} - {account.accountNumber} 
                          (${account.balance.toFixed(2)})
                        </option>
                      ))}
                    </select>
                    {transfer.fromAccountNumber && (
                      <small className="balance-hint">
                        Available: ${getAccountBalance(transfer.fromAccountNumber).toFixed(2)}
                      </small>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="toAccount">To Account *</label>
                    <select
                      id="toAccount"
                      value={transfer.toAccountNumber}
                      onChange={(e) => setTransfer({ ...transfer, toAccountNumber: e.target.value })}
                      required
                    >
                      <option value="">Select account</option>
                      {accounts
                        .filter(acc => acc.accountNumber !== transfer.fromAccountNumber)
                        .map((account) => (
                          <option key={account.id} value={account.accountNumber}>
                            {account.accountHolderName} - {account.accountNumber}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="amount">Amount *</label>
                    <input
                      type="number"
                      id="amount"
                      value={transfer.amount}
                      onChange={(e) => setTransfer({ ...transfer, amount: e.target.value })}
                      placeholder="0.00"
                      min="0.01"
                      step="0.01"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="description">Description (Optional)</label>
                    <input
                      type="text"
                      id="description"
                      value={transfer.description}
                      onChange={(e) => setTransfer({ ...transfer, description: e.target.value })}
                      placeholder="e.g., Payment for services"
                    />
                  </div>

                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Processing...' : '💸 Transfer Funds'}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </main>

      <footer className="footer">
        <div className="container">
          <p>Built with React & Spring Boot | Demo by Gillian Newton</p>
          <p>
            <a href="https://api.gilliannewton.com/swagger-ui.html" target="_blank" rel="noopener noreferrer">
              API Documentation
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;